import { successSession, requestSession, requestSessionDone, failureSession } from './sessionSlice'
import { successOrderCreate } from './checkoutSlice'
import { createSlice, createSelector } from '@reduxjs/toolkit'
import { successLogin } from './userSlice'

const idsArrayToObj = (ids) => ids?.reduce((acc, el) => {
  acc[el] = true;
  return acc;
}, {}) || {};

const initialState = {
  mobileApp: false,
  data: {
    products: [],
  },
  count: 0,
  ids: [],
  idsObj: {},
  loading: false,
  firstLoading: false,
  updating: false,
  updatingIds: {},
  error: null,
  promocodeError: null,
  certificateError: null,
  temporaryPromocode: null,
  temporaryCertificate: null,
}

const cart = createSlice({
  name: 'cart',
  initialState: initialState,
  reducers: {
    setMobileApp(state) {
      state.mobileApp = true
    },
    requestCart(state) {
      state.loading = true
      state.error = null
    },
    udpatingCart(state, { payload }) {
      const id = payload?.id;
      if (id) {
        state.updatingIds[id] = true;
      }

      state.updating = true
      state.error = null
    },
    removeCartProduct(state, { payload }) {
      const id = payload?.id;
      if (!id || !state.data?.products?.length) return;

      state.data.products = state.data.products.filter(el => el.external_id !== id)
    },
    endUpdatingCart(state, { payload }) {
      const id = payload?.id;
      if (id) {
        state.updatingIds[id] = false;
      }

      state.updating = false
    },
    failureUpdatingCart(state, { payload }) {
      const id = payload?.id;
      if (id) {
        state.updatingIds[id] = false;
      }

      state.updating = false
      state.error = payload?.error
    },
    firstRequestCart(state) {
      state.firstLoading = true
    },
    endRequestCart(state) {
      state.loading = false
    },
    successCart(state, { payload }) {
      const cart = payload
      const stopLoader = payload?.stopLoader ?? true
      state.count = payload.count ?? state.count
      state.data = cart
      state.firstLoading = false

      if (stopLoader) {
        state.loading = false
      }
      state.error = null
    },
    failureCart(state, { payload }) {
      state.loading = false
      state.firstLoading = false
      state.error = payload
    },
    requestPromocode(state) {
      state.loading = true
    },
    successPromocode(state, { payload }) {
      const cart = payload?.cart
      state.data = cart

      state.loading = false
      state.promocodeError = null
    },
    failurePromocode(state, { payload }) {
      state.loading = false
      state.promocodeError = payload
    },
    requestCertificate(state) {
      state.loading = true
    },
    successCertificate(state, { payload }) {
      const cart = payload?.cart
      state.data = cart

      state.loading = false
      state.certificateError = null
    },
    failureCertificate(state, { payload }) {
      state.loading = false
      state.certificateError = payload
    },
    saveTemporaryPromocode(state, { payload }) {
      state.temporaryPromocode = payload
    },
    resetTemporaryPromocode(state) {
      state.temporaryPromocode = null
    },
    saveTemporaryCertificate(state, { payload }) {
      state.temporaryCertificate = payload
    },
    resetTemporaryCertificate(state) {
      state.temporaryCertificate = null
    },
    updateIds(state, { payload }) {
      state.ids = payload
    },
    updateIdsObj(state, { payload }) {
      state.idsObj = payload
    },
    updateCount(state, { payload }) {
      state.count = payload
    },
    resetCart() {
      return { ...initialState }
    },
  },
  extraReducers: {
    [successSession]: _getCartFromSession,
    [successLogin]: _getCartFromSession,
    [successOrderCreate]: _getCartFromSession,
  },
})

function _getCartFromSession(state, { payload }) {
  const cart = payload?.cart ?? null
  const cart_count = payload?.cart_count ?? null

  if (cart) {
    state.ids = cart
    state.idsObj = idsArrayToObj(cart)
  }
  if (cart_count !== null) {
    state.count = cart_count
  }
  state.loading = false
}

export const getCartPromocode = (data) => {
  const promoCode = data?.promo_code
  const promoCodeDiscount = data?.total_promocard_discount_key

  return promoCode && promoCodeDiscount && promoCodeDiscount > 0 ? promoCode : null
}
export const getCartCertificatesAvaialble = (data, temporaryCertificate) => {
  const certificates = data?.certificates || []
  return [
    ...certificates,
    { certificate: temporaryCertificate, temporary: true },
  ].reduce((acc, cur) => {
    if (cur?.isAvailable || (cur.temporary && cur.certificate)) {
      acc.push({ ...cur })
    }
    return acc
  }, [])
}

export const getCartData = createSelector(
  state => state.cart,
  cartState => {
    const { data, loading, updating, error, promocodeError, temporaryPromocode, temporaryCertificate, count, ids, idsObj, firstLoading } =
      cartState

    return {
      data: {
        ...data,
        promo_code: getCartPromocode(data),
        certificates_available: getCartCertificatesAvaialble(data, temporaryCertificate)
      },
      // prevent any issues/changes on the frontend
      loading: loading || updating,
      error,
      promocodeError,
      temporaryPromocode,
      ids,
      idsObj,
      firstLoading,
      count,
    }
  },
)

export const getCartDataLoading = createSelector(
  state => state.cart,
  cartState => cartState.loading,
)

export const applyPromocode =
  ({ code, onSuccess, onError }) =>
    async (dispatch, getState, { api }) => {
      try {
        dispatch(requestPromocode())
        const response = await api.promocode({ promo_code: code })
        const { result, data } = response
        if (
          result === 'success' &&
          (data?.external_fetching ?? false) &&
          data?.total_promocard_discount_key > 0
        ) {
          dispatch(successPromocode({ cart: data }))
          if (onSuccess) onSuccess()
        } else {
          dispatch(failurePromocode(response))
          if (onError) onError()
        }
      } catch (err) {
        dispatch(failurePromocode(err.toString()))
        if (onError) onError()
      } finally {
        dispatch(resetTemporaryPromocode())
      }
    }

export const applyTemporaryPromocode =
  ({ code }) =>
    dispatch => {
      dispatch(saveTemporaryPromocode(code))
    }

export const removePromocode =
  ({ onSuccess, onError }) =>
    async (dispatch, getState, { api }) => {
      try {
        dispatch(requestPromocode())
        const response = await api.promocode({ promo_code: null })
        const { result, data } = response
        if (result === 'success' && (data?.external_fetching ?? false)) {
          dispatch(successPromocode({ cart: data }))
          if (onSuccess) onSuccess()
        } else {
          dispatch(failurePromocode(response))
          if (onError) onError()
        }
      } catch (err) {
        dispatch(failurePromocode(err.toString()))
        if (onError) onError()
      }
    }

export const applyCertificate =
  ({ code, onSuccess, onError }) =>
    async (dispatch, getState, { api }) => {
      try {
        dispatch(requestCertificate())
        const response = await api.certificateApply({ certificate: code })
        const { result, data } = response
        const newCertificateData = data?.certificates?.find(cert => cert.certificate === code) ?? {}

        if (
          result === 'success' &&
          (data?.external_fetching ?? false) &&
          newCertificateData?.isAvailable
        ) {
          dispatch(resetTemporaryCertificate())
          dispatch(successCertificate({ cart: data }))
          if (onSuccess) onSuccess(newCertificateData)
        } else {
          dispatch(resetTemporaryCertificate())
          if (result == 'error' && response.statusCode == 422) {
            dispatch(failureCertificate(response))
            if (onError) onError({ status: 'validation', message: response.errors.certificate[0] }, response)
          } else {
            dispatch(failureCertificate(response))
            if (onError) onError(newCertificateData, response)
          }
        }
      } catch (err) {
        dispatch(failureCertificate(err.toString()))
        if (onError) onError()
      }
    }

export const applyTemporaryCertificate =
  ({ code }) =>
    dispatch => {
      dispatch(saveTemporaryCertificate(code))
    }

export const removeCertificate =
  ({ code, onSuccess, onError }) =>
    async (dispatch, getState, { api }) => {
      try {
        dispatch(requestCertificate())
        const response = await api.certificateRemove({ certificate: code })
        const { result, data } = response
        if (result === 'success' && (data?.external_fetching ?? false)) {
          dispatch(successCertificate({ cart: data }))
          if (onSuccess) onSuccess()
        } else {
          dispatch(failureCertificate(response))
          if (onError) onError()
        }
      } catch (err) {
        dispatch(failureCertificate(err.toString()))
        if (onError) onError()
      }
    }

export const removeAllCertificates =
  ({ onSuccess = null, onError = null }) =>
    async (dispatch, getState, { api }) => {
      try {
        const response = await api.allCertificateRemove()
        const { result, data } = response
        if (result === 'success') {
          dispatch(successCart(data))
          if (onSuccess) onSuccess()
        } else {
          if (onError) onError()
        }
      } catch (err) {
        if (onError) onError(err)
      }
    }

const getCartCount = (cart) => {
  let count = 0;

  cart.products.forEach(({ cart_quantity }) => {
    if (cart_quantity) {
      count += cart_quantity;
    } else {
      count += 1;
    }
  })

  return count;
}
export const fetchCart = ({ onSuccess } = {}) => async (dispatch, getState, { api }) => {
  dispatch(requestCart())
  try {
    const resp = await api.cart()
    const { result = 'success' } = resp
    if (result === 'success') {
      // we don't need that for now
      // const response = await api.deliveryInfo()
      // const { resultDelivery = 'success'} = response
      // if (resultDelivery === 'success') {
      //   dispatch(successCart({ ...resp.data, deliveryDates: response.delivery_dates, count }))
      // } else {
      //   dispatch(successCart({ ...resp.data, count }))
      // }

      dispatch(successCart({ ...resp.data, count: getCartCount(resp.data) }))

      onSuccess?.(resp.data)
    } else {
      dispatch(failureCart(resp))
    }

  } catch (err) {
    dispatch(failureCart(err.toString()))
  }
}

export const fetchAsyncCart = ({ onSuccess } = {}) => async (dispatch, getState, { api }) => {
  dispatch(requestCart())
  try {
    const resp = await api.asyncCart()
    const { result = 'success' } = resp
    if (result === 'success') {
      dispatch(successCart({ ...resp.data, count: getCartCount(resp.data) }))

      onSuccess?.(resp.data)
    } else {
      dispatch(failureCart(resp))
    }

  } catch (err) {
    dispatch(failureCart(err.toString()))
  }
}

export const mergeCartData = ({ cartData }) => (dispatch, getState) => {
  dispatch(requestCart())
  try {
    if (!cartData) return;
    const prevData = getState().cart.data;
    const newProductsObj = cartData.products?.reduce((acc,el) => ({ ...acc, [el.external_id]: el }), {}) || {};

    const newData = {
      ...prevData,
      ...cartData,
      products: prevData.products.map(el => ({
        ...el,
        ...newProductsObj[el.external_id]
      })),
    };

    dispatch(successCart({ ...newData, count: getCartCount(cartData) }));
  } catch (err) {
    dispatch(failureCart(err.toString()));
  }
}

export const setLoadingRequest = () => async (dispatch, getState, { api }) => {
  dispatch(firstRequestCart())
}

export const createInCart = ({ id, quantity, mindboxDeviceUUID, onSuccess, onError, meta }) => async (dispatch, getState, { api }) => {
  // we don't use udpatingCart here 'cause createInCart is used outside of cart
  dispatch(requestCart())
  try {
    const resp = await api.addToCart({ id, quantity, mindboxDeviceUUID, meta })
    const { result = 'success' } = resp
    if (result === 'success') {
      dispatch(endRequestCart())
      if (onSuccess) onSuccess()
    } else {
      dispatch(failureCart(resp))
      onError?.(resp)
    }
  } catch (err) {
    dispatch(failureCart(err.toString()))
    onError?.(err)
  }
}

export const removeFromCart = ({ id, mindboxDeviceUUID, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  dispatch(udpatingCart({ id }))
  try {
    const resp = await api.removeFromCart({ product_id: id, mindboxDeviceUUID })
    const { result = 'success' } = resp

    if (result === 'success') {
      dispatch(endUpdatingCart({ id }))
      if (onSuccess) onSuccess()
    } else {
      dispatch(failureUpdatingCart({ error: resp, id }))
      if (onError) onError(resp)
    }

  } catch (err) {
    dispatch(failureUpdatingCart({ error: err.toString(), id }))
    if (onError) onError(err)
  }
}

export const updateCart = ({ id, quantity, onSuccess, onError, mindboxDeviceUUID }) => async (dispatch, getState, { api }) => {
  dispatch(udpatingCart({ id }))
  try {
    const resp = await api.updateCart({ id, quantity, mindboxDeviceUUID })
    const { result = 'success' } = resp
    if (result === 'success') {
      dispatch(endUpdatingCart({ id }))
      if (onSuccess) onSuccess()
    } else {
      dispatch(failureUpdatingCart({ error: resp, id }))
      if (onError) onError()
    }
  } catch (err) {
    dispatch(failureUpdatingCart({ error: err.toString(), id }))
    if (onError) onError()
  }
}

export const createLookInCart = ({ products, mindboxDeviceUUID, onSuccess, onError, meta, originalError = false }) => async (dispatch, getState, { api }) => {
  dispatch(requestCart())
  try {
    const resp = await api.addLookToCart({ products, mindboxDeviceUUID, meta })
    const { result = 'success' } = resp
    if (result === 'success') {
      dispatch(endRequestCart())
      if (onSuccess) onSuccess()
    } else {
      dispatch(failureCart(resp))
      onError?.(resp)
    }
  } catch (err) {
    dispatch(failureCart(originalError ? err : err.toString()))
    onError?.(originalError ? err : err.toString())
  }
}


export const updateCartIds = ({ ids }) => (dispatch, getState) => {
  dispatch(updateIds(ids))
}

export const updateCartCount = ({ count }) => (dispatch, getState) => {
  dispatch(updateCount(count))
}

export const removeCartId =
  ({ id }) =>
    (dispatch, getState) => {
      const state = getState().cart
      const ids = state.ids


      dispatch(updateIds(ids.filter(item => item != id)))
    }

export const addCartId =
  ({ id }) =>
    (dispatch, getState) => {
      const state = getState().cart
      const ids = state.ids

      const hasId = ids.some(item => item == id)

      if (!hasId) {
        dispatch(updateIds([...ids, id]))
      }
    }


export const setNewCartData = (data) => async (dispatch, _getState, { api }) => {
  dispatch(successCart(data))
}

export const {
  setMobileApp,
  requestCart,
  udpatingCart,
  updateIds,
  updateIdsObj,
  firstRequestCart,
  endRequestCart,
  endUpdatingCart,
  failureUpdatingCart,
  removeCartProduct,
  successCart,
  failureCart,
  updateCount,
  requestPromocode,
  successPromocode,
  failurePromocode,
  requestCertificate,
  successCertificate,
  failureCertificate,
  saveTemporaryPromocode,
  resetTemporaryPromocode,
  saveTemporaryCertificate,
  resetTemporaryCertificate,
  resetCart,
} = cart.actions

export default cart.reducer
